import { z } from 'zod';
const projectStates = ['PLANNED', 'STARTED', 'IN_PROGRESS', 'COMPLETED', 'CANCELED'];
const taskStates = ['PLANNED', 'STARTED', 'IN_PROGRESS', 'COMPLETED', 'CANCELED'];
const milestoneStates = ['CREATED', 'STARTED', 'SUSPENDED', 'CANCELED', 'COMPLETED'];
const priorities = ['URGENT', 'HIGH', 'NORMAL', 'LOW'];
const billTypes = ['BILLABLE', 'BLANKET'];
const parentTypes = ['PROJECT', 'MILESTONE', 'TASK'];
const milestoneRanks = ['START', 'NORMAL', 'END'];
const costElementTypes = ['COWORKERS', 'SERVICES', 'UNIT'];
const participantRoles = ['LEAD', 'ASSISTANT'];
// Project Types
export const createProjectTypeSchema = z.object({
    name: z.string().min(1).max(255),
    label: z.string().min(1).max(255),
});
export const updateProjectTypeSchema = createProjectTypeSchema.partial();
// Cost Elements
export const createCostElementSchema = z.object({
    name: z.string().min(1).max(255),
    external_ref: z.string().max(255).nullable().optional(),
    charged: z.boolean().optional().default(false),
    type: z.enum(costElementTypes).default('COWORKERS'),
});
export const updateCostElementSchema = createCostElementSchema.partial();
// Projects
export const createProjectSchema = z.object({
    name: z.string().min(1).max(255),
    nickname: z.string().max(120).nullable().optional(),
    description: z.string().nullable().optional(),
    customer_id: z.string().uuid().nullable().optional(),
    customer_cost_centre_id: z.string().uuid().nullable().optional(),
    project_type_id: z.number().int().positive().nullable().optional(),
    link_uri: z.string().url().nullable().optional().or(z.literal('')),
    priority: z.enum(priorities).default('NORMAL'),
    deadline: z.string().nullable().optional(),
    project_no: z.string().max(100).nullable().optional(),
    project_reference: z.string().max(255).nullable().optional(),
    state: z.enum(projectStates).default('PLANNED'),
    bill_type: z.enum(billTypes).nullable().optional(),
    planned_start: z.string().nullable().optional(),
    planned_end: z.string().nullable().optional(),
});
export const updateProjectSchema = createProjectSchema.partial();
// Tasks
export const createTaskSchema = z.object({
    name: z.string().min(1).max(255),
    info: z.string().nullable().optional(),
    planned_hours: z.number().min(0).nullable().optional(),
    planned_quantity: z.number().int().min(0).nullable().optional(),
    parent_type: z.enum(parentTypes),
    parent_id: z.string().uuid(),
    state: z.enum(taskStates).default('PLANNED'),
    priority: z.enum(priorities).default('NORMAL'),
    bill_type: z.enum(billTypes).nullable().optional(),
    cost_element_id: z.number().int().positive().nullable().optional(),
    task_no: z.string().max(100).nullable().optional(),
    link_uri: z.string().url().nullable().optional().or(z.literal('')),
});
export const updateTaskSchema = createTaskSchema.partial();
export const moveTaskSchema = z.object({
    parent_type: z.enum(parentTypes),
    parent_id: z.string().uuid(),
    seq: z.number().int().min(0).optional(),
});
export const reorderTasksSchema = z.object({
    task_ids: z.array(z.string().uuid()).min(1),
});
// Milestones
export const createMilestoneSchema = z.object({
    name: z.string().min(1).max(255),
    description: z.string().nullable().optional(),
    state: z.enum(milestoneStates).default('CREATED'),
    responsible_coworker_id: z.string().uuid().nullable().optional(),
    deadline: z.string().nullable().optional(),
    rank: z.enum(milestoneRanks).default('NORMAL'),
});
export const updateMilestoneSchema = createMilestoneSchema.partial();
// Work Logs
export const createWorkLogSchema = z.object({
    date: z.string(),
    duration: z.number().int().min(0), // seconds
    quantity: z.number().int().min(0).optional().default(0),
    coworker_id: z.string().uuid(),
    task_id: z.string().uuid().nullable().optional(),
    ticket_id: z.string().uuid().nullable().optional(),
    description: z.string().min(1),
    overtime: z.boolean().optional().default(false),
    surcharge: z.boolean().optional().default(false),
    on_call_duty: z.boolean().optional().default(false),
    discount: z.number().min(0).optional().default(0),
    start_time: z.string().nullable().optional(),
    end_time: z.string().nullable().optional(),
});
export const updateWorkLogSchema = createWorkLogSchema.partial();
export const bookWorkLogsSchema = z.object({
    work_log_ids: z.array(z.number().int().positive()).min(1),
    booked_date: z.string(),
    booked_invoice_no: z.string().max(100),
    booked_line_no: z.number().int().positive().optional(),
    booked_discount: z.number().min(0).optional(),
    booked_quantity: z.number().min(0).optional(),
    booked_amount: z.number().min(0).optional(),
    booked_unit_price: z.number().min(0).optional(),
    booked_unit_code: z.string().max(50).optional(),
});
// Participants
export const addParticipantSchema = z.object({
    coworker_id: z.string().uuid(),
    role: z.enum(participantRoles).default('ASSISTANT'),
});
// Query schemas
export const projectListQuerySchema = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).optional().default(25),
    sort_by: z.string().optional().default('created_at'),
    sort_order: z.enum(['asc', 'desc']).optional().default('desc'),
    state: z.enum(projectStates).optional(),
    priority: z.enum(priorities).optional(),
    project_type_id: z.coerce.number().int().optional(),
    customer_id: z.string().uuid().optional(),
    search: z.string().optional(),
});
export const workLogListQuerySchema = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).optional().default(25),
    from: z.string().optional(),
    to: z.string().optional(),
    coworker_id: z.string().uuid().optional(),
    task_id: z.string().uuid().optional(),
});
// ===== Phase C =====
const anfKategorien = [
    'Funktional',
    'Nicht-funktional',
    'Schnittstelle',
    'Compliance',
    'UX/UI',
    'Performance',
];
const anfStatuses = ['Neu', 'In Planung', 'Umgesetzt', 'Abgenommen'];
export const createAnforderungSchema = z.object({
    nr: z.string().trim().max(20).nullable().optional(),
    role: z.string().trim().max(255).nullable().optional(),
    ziel: z.string().min(1),
    nutzen: z.string().trim().nullable().optional(),
    kategorie: z.enum(anfKategorien).nullable().optional(),
    story_points: z.number().int().min(0).nullable().optional(),
    status: z.enum(anfStatuses).default('Neu'),
});
export const updateAnforderungSchema = createAnforderungSchema.partial();
// Stakeholders
const stakeholderTypes = ['group', 'person', 'extern'];
export const createStakeholderSchema = z.object({
    name: z.string().min(1).max(255),
    role: z.string().max(255).nullable().optional(),
    type: z.enum(stakeholderTypes).default('person'),
    color: z.string().max(20).nullable().optional(),
    power: z.number().int().min(1).max(5).default(3),
    interest: z.number().int().min(1).max(5).default(3),
    strategy_override: z.boolean().default(false),
    matrix_x: z.number().default(0),
    matrix_y: z.number().default(0),
    categories: z.array(z.string()).default([]),
    notes: z.string().nullable().optional(),
});
export const updateStakeholderSchema = createStakeholderSchema.partial();
// Risiken
const riskStatuses = ['Identifiziert', 'Bewertet', 'Geschlossen'];
const riskStrategies = ['Vermindern', 'Vermeiden', 'Akzeptieren', 'Eskalieren'];
export const createRisikoSchema = z.object({
    beschreibung: z.string().min(1),
    weight: z.number().int().min(1).max(5).default(3),
    assessment: z.number().int().min(1).max(5).default(3),
    strategie: z.enum(riskStrategies).nullable().optional(),
    action: z.string().nullable().optional(),
    status: z.enum(riskStatuses).default('Identifiziert'),
});
export const updateRisikoSchema = createRisikoSchema.partial();
// Meetings
const meetingTypes = ['Kick-Off', 'Workshop', 'Status-Meeting', 'Review', 'Retrospektive', 'Andere'];
export const createMeetingSchema = z.object({
    titel: z.string().nullable().optional(),
    typ: z.enum(meetingTypes).nullable().optional(),
    datum: z.string(),
    teilnehmer: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
});
export const updateMeetingSchema = createMeetingSchema.partial();
// Offene Punkte
const opKategorien = ['Allgemein', 'Risiko', 'Anforderung', 'Arbeitspaket', 'Meilenstein'];
const opStatuses = ['Offen', 'In Klärung', 'Beantwortet', 'Geschlossen'];
export const createOffenerPunktSchema = z.object({
    titel: z.string().nullable().optional(),
    kategorie: z.enum(opKategorien).nullable().optional(),
    verantwortlicher_coworker_id: z.string().uuid().nullable().optional(),
    faellig: z.string().nullable().optional(),
    status: z.enum(opStatuses).default('Offen'),
    beschreibung: z.string().nullable().optional(),
    related_entity_type: z.string().nullable().optional(),
    related_entity_id: z.string().nullable().optional(),
});
export const updateOffenerPunktSchema = createOffenerPunktSchema.partial();
// Project Team
export const createProjectTeamMemberSchema = z.object({
    coworker_id: z.string().uuid().nullable().optional(),
    name: z.string().min(1).max(255),
    rolle: z.string().nullable().optional(),
    funktion: z.string().max(120).nullable().optional(),
    auslastung: z.number().min(0).max(100).nullable().optional(),
    email: z.string().email().nullable().optional(),
});
export const updateProjectTeamMemberSchema = createProjectTeamMemberSchema.partial();
// Retro Items
const retroColumns = ['good', 'imp', 'idea', 'act'];
export const createRetroItemSchema = z.object({
    spalte: z.enum(retroColumns),
    inhalt: z.string().min(1),
    author_coworker_id: z.string().uuid().nullable().optional(),
});
export const updateRetroItemSchema = createRetroItemSchema.partial();
// Skill Names
export const createSkillNameSchema = z.object({
    name: z.string().min(1).max(255),
});
// Arbeitspakete
const apStatuses = ['Offen', 'In Arbeit', 'Erledigt', 'Blockiert'];
export const createArbeitspaketSchema = z.object({
    nr: z.string().min(1).max(50),
    titel: z.string().nullable().optional(),
    beschreibung: z.string().nullable().optional(),
    leistungsbeschreibung: z.string().trim().nullable().optional(),
    dod: z.string().nullable().optional(),
    resp_coworker_id: z.string().uuid().nullable().optional(),
    acc_coworker_id: z.string().uuid().nullable().optional(),
    con_coworker_ids: z.array(z.string().uuid()).default([]),
    inf_coworker_ids: z.array(z.string().uuid()).default([]),
    // RACI als Freitext (keine Coworker-Referenz). TEXT ohne Cap, analog beschreibung/dod.
    raci_r: z.string().trim().nullable().optional(),
    raci_a: z.string().trim().nullable().optional(),
    raci_c: z.string().trim().nullable().optional(),
    raci_i: z.string().trim().nullable().optional(),
    start_date: z.string().nullable().optional(),
    end_date: z.string().nullable().optional(),
    story_points: z.number().int().min(0).nullable().optional(),
    status: z.enum(apStatuses).default('Offen'),
    progress: z.number().int().min(0).max(100).default(0),
    is_milestone: z.boolean().default(false),
});
export const updateArbeitspaketSchema = createArbeitspaketSchema.partial();
// Weekly Progress
export const createWeeklyProgressSchema = z.object({
    kw: z.number().int().min(1).max(53),
    jahr: z.number().int().min(2000).max(2100),
    neu: z.number().int().min(0).default(0),
    offen: z.number().int().min(0).default(0),
    wip: z.number().int().min(0).default(0),
    erledigt: z.number().int().min(0).default(0),
    snapshot_date: z.string(),
});
export const updateWeeklyProgressSchema = createWeeklyProgressSchema.partial();
// Schritt-Reifegrad
const eskalationLevels = ['g', 'y', 'r'];
export const upsertSchrittReifegradSchema = z.object({
    schritt: z.number().int().min(1).max(18),
    reifegrad: z.number().int().min(0).max(5),
    eskalation: z.enum(eskalationLevels).nullable().optional(),
    notes: z.string().nullable().optional(),
});
// Project Config
const magicTriangleAxes = ['scope', 'cost', 'schedule'];
export const updateProjectConfigSchema = z.object({
    magic_triangle_fixed: z.array(z.enum(magicTriangleAxes)).optional(),
    magic_triangle_flex: z.array(z.enum(magicTriangleAxes)).optional(),
    stacey_x: z.number().min(0).max(10).optional(),
    stacey_y: z.number().min(0).max(10).optional(),
    status_report: z.record(z.unknown()).optional(),
}).refine((val) => {
    if (!val.magic_triangle_fixed || !val.magic_triangle_flex)
        return true;
    const all = [...val.magic_triangle_fixed, ...val.magic_triangle_flex];
    const allUnique = new Set(all).size === all.length;
    const allCovered = all.length === 3 && new Set(all).size === 3;
    return allUnique && allCovered && val.magic_triangle_flex.length >= 1;
}, { message: 'magic_triangle_fixed und _flex müssen disjunkt {scope,cost,schedule} abdecken, mindestens 1 flex' });
//# sourceMappingURL=project.js.map